// =============================================================================
// FarmSmartly — Pure stat computation helpers
// All functions take state arrays as arguments and return derived values.
// No mock data. No hardcoded IDs.
// OPTIMIZED: Added memoization for expensive calculations
// =============================================================================

import { CATEGORIES } from '../config/farmConfig';

// Use local date — toISOString() gives UTC which shifts the date for UTC+ timezones
const _now = new Date();
export const TODAY = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;


// Simple memoization cache for expensive calculations
const memoCache = new Map();
const MAX_CACHE_SIZE = 100;

/** Clear all memoized stat results — call after any AppContext mutation. */
export function clearMemoCache() {
  memoCache.clear();
}

/**
 * Format a local Date object as YYYY-MM-DD without UTC shift.
 * Use instead of date.toISOString().split('T')[0].
 */
export function localDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Memoize function results based on a key derived from arguments
 * @param {string} key - Cache key
 * @param {Function} fn - Function to execute
 * @returns {*} - Function result
 */
function memoized(key, fn) {
  if (memoCache.has(key)) {
    return memoCache.get(key);
  }

  // Clear oldest entries if cache is full
  if (memoCache.size >= MAX_CACHE_SIZE) {
    const firstKey = memoCache.keys().next().value;
    memoCache.delete(firstKey);
  }

  const result = fn();
  memoCache.set(key, result);
  return result;
}

/**
 * Generate cache key — hashes arrays by their first+last item id (not just length)
 * so the key changes when records are edited while count stays the same.
 */
function generateKey(fnName, args) {
  const argsHash = args.map(arg => {
    if (Array.isArray(arg)) {
      const first = arg[0]?.id ?? '';
      const last  = arg[arg.length - 1]?.id ?? '';
      return `arr:${arg.length}:${first}:${last}`;
    }
    if (arg && typeof arg === 'object') return `obj:${Object.keys(arg).length}`;
    return String(arg);
  }).join('|');
  return `${fnName}:${argsHash}:${TODAY}`;
}

// ── Snapshot helpers ──────────────────────────────────────────────────────────

export function getTodayEggs(eggRecords) {
  return eggRecords
    .filter((r) => r.date === TODAY)
    .reduce((s, r) => s + r.count, 0);
}

export function getTodayExpenses(expenses) {
  return expenses
    .filter((e) => e.date === TODAY)
    .reduce((s, e) => s + e.amount, 0);
}

export function getActiveIncubatorEggs(batches) {
  return batches
    .filter((b) => b.status !== 'completed')
    .reduce((s, b) => s + b.eggsIn - b.damaged - b.failed, 0);
}

export function getRoomTodayEggs(eggRecords, roomId) {
  return eggRecords
    .filter((e) => e.date === TODAY && e.roomId === roomId)
    .reduce((s, e) => s + e.count, 0);
}

export function getFlockTodayEggs(eggRecords, flockId) {
  const r = eggRecords.find((e) => e.date === TODAY && e.flockId === flockId);
  return r ? r.count : 0;
}

export function getTotalSalesIncome(sales) {
  return sales.reduce((s, sale) => s + sale.total, 0);
}

// ── Trend helpers (last 7 days) ───────────────────────────────────────────────
// OPTIMIZED: Wrapped with memoization to prevent repeated expensive calculations

export function getLast7DaysEggTrend(eggRecords) {
  return memoized(generateKey('eggTrend', [eggRecords]), () => {
    const [y, mo, dy] = TODAY.split('-').map(Number);
    return Array.from({ length: 7 }, (_, i) => {
      const d     = new Date(y, mo - 1, dy - (6 - i));
      const date  = localDateStr(d);
      const label = d.toLocaleDateString('en', { weekday: 'short' });
      const records = eggRecords.filter((r) => r.date === date);
      return {
        date,
        label,
        total: records.reduce((s, r) => s + r.count, 0),
      };
    });
  });
}

export function getLast7DaysExpenses(expenses) {
  return memoized(generateKey('expTrend', [expenses]), () => {
    const [y, mo, dy] = TODAY.split('-').map(Number);
    return Array.from({ length: 7 }, (_, i) => {
      const d      = new Date(y, mo - 1, dy - (6 - i));
      const date   = localDateStr(d);
      const label  = d.toLocaleDateString('en', { weekday: 'short' });
      const dayExp = expenses.filter((e) => e.date === date);
      const result = { date, label, total: 0 };
      CATEGORIES.forEach((cat) => {
        result[cat]   = dayExp.filter((e) => e.category === cat).reduce((s, e) => s + e.amount, 0);
        result.total += result[cat];
      });
      return result;
    });
  });
}

export function getLast7DaysSalesTrend(sales) {
  return memoized(generateKey('salesTrend', [sales]), () => {
    const [y, mo, dy] = TODAY.split('-').map(Number);
    return Array.from({ length: 7 }, (_, i) => {
      const d        = new Date(y, mo - 1, dy - (6 - i));
      const date     = localDateStr(d);
      const label    = d.toLocaleDateString('en', { weekday: 'short' });
      const daySales = sales.filter((s) => s.date === date);
      return {
        date,
        label,
        total:     daySales.reduce((s, x) => s + x.total, 0),
        eggSales:  daySales.filter((x) => x.type === 'egg').reduce((s, x)  => s + x.total, 0),
        birdSales: daySales.filter((x) => x.type === 'bird').reduce((s, x) => s + x.total, 0),
      };
    });
  });
}

export function getExpensesByCategory(expenses) {
  return memoized(generateKey('expByCat', [expenses]), () =>
    CATEGORIES.map((cat) => ({
      category: cat,
      total: expenses.filter((e) => e.category === cat).reduce((s, e) => s + e.amount, 0),
    }))
  );
}

// ── Feed helpers ──────────────────────────────────────────────────────────────

/**
 * Total feed consumed per feed type over the last N days (default 7).
 * Returns array: [{ feedType, totalKg }] sorted descending by totalKg.
 */
export function getFeedConsumptionSummary(feedRecords, days = 7) {
  const cutoff = (() => {
    const d = new Date();
    d.setDate(d.getDate() - (days - 1));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const recent = feedRecords.filter((r) => r.date >= cutoff && r.date <= TODAY);
  const byType = {};
  for (const r of recent) {
    byType[r.feedType] = (byType[r.feedType] || 0) + (r.qtyKg || 0);
  }
  return Object.entries(byType)
    .map(([feedType, totalKg]) => ({ feedType, totalKg: Math.round(totalKg * 10) / 10 }))
    .sort((a, b) => b.totalKg - a.totalKg);
}

/**
 * Feed conversion ratio for a single flock (eggs per kg of feed).
 * Higher = more efficient. Returns null if no feed records exist.
 */
export function getFeedConversionRatio(feedRecords, eggRecords, flockId) {
  const totalFeedKg = feedRecords
    .filter((r) => r.flockId === flockId)
    .reduce((s, r) => s + (r.qtyKg || 0), 0);
  if (totalFeedKg === 0) return null;
  const totalEggs = eggRecords
    .filter((r) => r.flockId === flockId)
    .reduce((s, r) => s + r.count, 0);
  return Math.round((totalEggs / totalFeedKg) * 10) / 10; // eggs per kg, 1 decimal
}

// ── Age calculation helpers ───────────────────────────────────────────────────

/**
 * Calculate days since flock start date
 * @param {string} startDate - ISO date string (YYYY-MM-DD)
 * @returns {number} - Days since start date
 */
export function getFlockAgeInDays(startDate) {
  if (!startDate) return 0;
  const start = new Date(startDate);
  const now = new Date(TODAY);
  const diffTime = now - start;
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Format flock age for display
 * @param {string} startDate - ISO date string (YYYY-MM-DD)
 * @returns {string} - Human readable age (e.g., "5 days", "2 months", "1y 3m")
 */
export function formatFlockAge(startDate) {
  if (!startDate) return 'Unknown';
  const days = getFlockAgeInDays(startDate);
  if (days < 0) return 'Future date';
  if (days === 0) return 'Today';
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''}`;
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (remainingMonths === 0) return `${years} year${years !== 1 ? 's' : ''}`;
  return `${years}y ${remainingMonths}m`;
}

// Compact form of formatFlockAge for narrow fixed-width UI (e.g. stat pills)
// where "5 months" gets clipped but "5mo" fits.
export function formatFlockAgeShort(startDate) {
  if (!startDate) return '—';
  const days = getFlockAgeInDays(startDate);
  if (days < 0) return '—';
  if (days === 0) return 'Today';
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  return remainingMonths === 0 ? `${years}y` : `${years}y${remainingMonths}m`;
}
